// One-time disclosure toast for ad-arrived visitors: the moment the server
// first acknowledges an engagement milestone (`StoredEngagement.reportedTier`
// crossing 0 — the same acknowledgement `src/lib/engagement/` stores), the
// reader is told, once, that milestones are reported to Google Ads, with a
// link to the privacy note that says so at length.
//
// Scoped to GOOGLE arrivals only: the disclosure exists because reporting
// happens, and reporting only happens for a stored Google click id. An
// organic visitor (or an `msclkid` arrival — Microsoft is not uploaded,
// `convex/model/conversionUpload.ts`) gets no toast because nothing about
// them is reported.
//
// The check runs on navigation rather than on a timer: the first tier is
// itself crossed by navigating (T1) or acknowledged shortly after a chapter
// is finished (T2), so the next route change is at most one page late — and
// it never interrupts mid-chapter reading. Missing engagement state, a
// blocked localStorage, or a tracker that hasn't shipped all degrade to
// "no toast", never to an error.

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { getPref, setPref, PREF_KEYS } from "@/lib/prefs";

/** The Google members of `CLICK_ID_PARAMS` (`src/lib/attribution.ts`)
 *  — the sources the conversion pipeline actually uploads. */
const GOOGLE_CLICK_SOURCES = ["gclid", "gbraid", "wbraid"];

export function AdPrivacyNotice() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (getPref(PREF_KEYS.privacyNoticeShownAt, null) !== null) return;

    const click = getPref(PREF_KEYS.clickId, null);
    if (click === null || !GOOGLE_CLICK_SOURCES.includes(click.source)) return;

    const engagement = getPref(PREF_KEYS.engagement, null);
    if (engagement === null || engagement.reportedTier < 1) return;

    // Flag before toasting so a remount (StrictMode, layout crossing) can't
    // re-fire; the stable id collapses any same-tick double render anyway.
    setPref(PREF_KEYS.privacyNoticeShownAt, Date.now());
    toast("A note on privacy", {
      id: "ad-privacy-notice",
      duration: 12_000,
      description:
        "You arrived from an ad, so reading milestones — like the one you " +
        "just passed — are reported to Google Ads to measure whether that " +
        "ad earns its keep. No name, no reading list; just the milestone.",
      action: {
        label: "Read the note",
        onClick: () => {
          void navigate("/privacy");
        },
      },
    });
  }, [pathname, navigate]);

  return null;
}
